/**
 * Shared parent/program enrollment filter rules for Manage Parent & Manage Program.
 *
 * "Ongoing" cohort (Manage Cohort UI): cohort.isStarted === true
 * "Active" enrollment: paid program on an Ongoing cohort that is not completed
 */

/** Cohort is Ongoing (started and running). */
function isOngoingCohort(cohort) {
  return !!cohort && cohort.isStarted === true;
}

/**
 * Program counts as an active enrollment:
 * paid + not completed + cohort Ongoing.
 */
function isActiveEnrollmentProgram(program) {
  if (!program) return false;
  return (
    program.isPaid === true &&
    program.isCompleted === false &&
    isOngoingCohort(program.cohort)
  );
}

function getParentPrograms(parent) {
  const children = Array.isArray(parent?.children) ? parent.children : [];
  return children.flatMap((child) =>
    (Array.isArray(child?.programs) ? child.programs : []).map((program) => ({
      child,
      program,
    }))
  );
}

/** Parent has at least one paid, incomplete program on an Ongoing cohort. */
function parentHasActiveChildEnrollment(parent) {
  return getParentPrograms(parent).some(({ program }) =>
    isActiveEnrollmentProgram(program)
  );
}

/** Parent has at least one paid completed program (past cohort completion). */
function parentHasCompletedPastCohort(parent) {
  return getParentPrograms(parent).some(
    ({ program }) => program?.isPaid === true && program?.isCompleted === true
  );
}

function parentHasAnyCompletedProgram(parent) {
  return getParentPrograms(parent).some(
    ({ program }) => program?.isCompleted === true
  );
}

/**
 * Cohort status dropdown on Manage Parent.
 * mode: "active" | "no-active" | "alumni" | "completed-inactive"
 */
function filterParentsByCohortMode(parents, mode) {
  const list = Array.isArray(parents) ? parents : [];
  switch (mode) {
    case "active":
      return list.filter((p) => parentHasActiveChildEnrollment(p));
    case "no-active":
      return list.filter((p) => !parentHasActiveChildEnrollment(p));
    case "alumni":
      return list.filter((p) => {
        const rows = getParentPrograms(p);
        if (rows.length === 0) return false;
        return rows.every(({ program }) => program?.isCompleted === true);
      });
    case "completed-inactive":
      return list.filter(
        (p) =>
          parentHasCompletedPastCohort(p) && !parentHasActiveChildEnrollment(p)
      );
    default:
      return list;
  }
}

/** Completion dropdown: 0 all | 1 with completed | 2 without completed */
function filterParentsByCompletion(parents, mode) {
  const list = Array.isArray(parents) ? parents : [];
  if (Number(mode) === 1) return list.filter((p) => parentHasAnyCompletedProgram(p));
  if (Number(mode) === 2) return list.filter((p) => !parentHasAnyCompletedProgram(p));
  return list;
}

/** Referral dropdown: 0 all | 1 normal (no referrer) | 2 referred */
function filterParentsByReferral(parents, mode) {
  const list = Array.isArray(parents) ? parents : [];
  if (Number(mode) === 1) return list.filter((p) => p?.referredBy == null);
  if (Number(mode) === 2) return list.filter((p) => p?.referredBy != null);
  return list;
}

/** Promo parent type: 0 all | 1 isExternal === 1 */
function filterParentsByExternal(parents, mode) {
  const list = Array.isArray(parents) ? parents : [];
  if (Number(mode) === 1) return list.filter((p) => Number(p?.isExternal) === 1);
  return list;
}

/** Privacy: 0 all | 1 private | 2 public */
function filterParentsByPrivacy(parents, mode) {
  const list = Array.isArray(parents) ? parents : [];
  if (Number(mode) === 1) return list.filter((p) => p?.privacyMode === true);
  if (Number(mode) === 2) return list.filter((p) => p?.privacyMode === false);
  return list;
}

function filterParentsByAssignedAdmin(parents, selectedAdminId) {
  const list = Array.isArray(parents) ? parents : [];
  if (!selectedAdminId) return list;
  if (selectedAdminId === "unassigned") {
    return list.filter((parent) => !parent?.assignedTo);
  }
  return list.filter(
    (parent) =>
      String(parent?.assignedTo || parent?.admin?.id || "") ===
      String(selectedAdminId)
  );
}

/**
 * Stacked Manage Parent table filters (mirrors applyParentTableFilters).
 */
function applyManageParentFilters(parents, filters = {}) {
  const {
    assignedAdminFilter = "",
    pastCompletedFilter = "",
    activeChildFilter = "",
    subscribedFilter = "",
    cohortMode = "",
    completionMode = 0,
    referralMode = 0,
    externalMode = 0,
    privacyMode = 0,
  } = filters;

  let list = Array.isArray(parents) ? parents : [];
  if (cohortMode) list = filterParentsByCohortMode(list, cohortMode);
  list = filterParentsByCompletion(list, completionMode);
  list = filterParentsByReferral(list, referralMode);
  list = filterParentsByExternal(list, externalMode);
  list = filterParentsByPrivacy(list, privacyMode);

  list = list.filter((parent) => {
    const matchPast =
      !pastCompletedFilter ||
      (pastCompletedFilter === "yes"
        ? parentHasCompletedPastCohort(parent)
        : !parentHasCompletedPastCohort(parent));
    const matchActive =
      !activeChildFilter ||
      (activeChildFilter === "yes"
        ? parentHasActiveChildEnrollment(parent)
        : !parentHasActiveChildEnrollment(parent));
    const matchSubscribed =
      !subscribedFilter ||
      (subscribedFilter === "yes"
        ? parent.isSubscribed === true
        : parent.isSubscribed !== true);
    return matchPast && matchActive && matchSubscribed;
  });

  return filterParentsByAssignedAdmin(list, assignedAdminFilter);
}

/**
 * Manage Program status filter.
 * status: 0 all | 1 active enrolment | 2 awaiting checkout | 3 paid with promo
 */
function filterProgramsByStatus(programs, status) {
  const list = Array.isArray(programs) ? programs : [];
  if (status === 1) {
    return list.filter((p) => isActiveEnrollmentProgram(p));
  }
  if (status === 2) {
    return list.filter(
      (p) => p?.isPaid === false && p?.isCompleted === false
    );
  }
  if (status === 3) {
    return list.filter((p) => p?.paidWithPromo === true);
  }
  return list;
}

/**
 * Apply Manage Program table filters (pure).
 */
function applyManageProgramFilters(rawData, filters = {}) {
  if (!Array.isArray(rawData)) {
    return { display: [], filtered0: [], filtered2: [] };
  }

  const {
    searchQuery = "",
    filterCohortId = 0,
    filterStatus = 0,
    filterPackageId = 0,
    filterCouponId = 0,
    filterTeacherAssign = 0,
    filterTeacherId = 0,
    filterPaidUnpaid = 0,
    filterConflictPrograms = 0,
    filterAlertPrograms = 0,
    filterCertificateSent = 0,
  } = filters;

  let filtered0 = rawData;
  if (searchQuery) {
    const q = String(searchQuery).toLowerCase();
    filtered0 = rawData.filter((p) =>
      `${p?.child?.firstName || ""} ${p?.child?.lastName || ""} ${p?.child?.parent?.firstName || ""} ${p?.child?.parent?.lastName || ""}`
        .toLowerCase()
        .includes(q)
    );
  }

  if (Number(filterCohortId) > 0) {
    filtered0 = filtered0.filter(
      (r) => Number(r.cohortId) === Number(filterCohortId)
    );
  }

  let filtered2 = filterProgramsByStatus(filtered0, Number(filterStatus));
  let display = filtered2;

  if (Number(filterPackageId) > 0) {
    display = display.filter(
      (p) => Number(p?.package?.id) === Number(filterPackageId)
    );
  }
  if (Number(filterCouponId) > 0) {
    display = display.filter(
      (p) => Number(p?.coupon?.id) === Number(filterCouponId)
    );
  }
  if (Number(filterTeacherAssign) === 1) {
    display = display.filter((r) => r.teacherId != null);
  } else if (Number(filterTeacherAssign) === 2) {
    display = display.filter((r) => r.teacherId == null);
  }
  if (Number(filterTeacherId) > 0) {
    display = display.filter(
      (r) => Number(r.teacherId) === Number(filterTeacherId)
    );
  }
  if (Number(filterPaidUnpaid) === 1) {
    display = display.filter((r) => r.isPaid === true);
  } else if (Number(filterPaidUnpaid) === 2) {
    display = display.filter((r) => r.isPaid === false);
  }
  if (Number(filterConflictPrograms) === 1) {
    display = display.filter((r) => r?.teacherScheduleConflict === true);
  } else if (Number(filterConflictPrograms) === 2) {
    display = display.filter((r) => r?.teacherScheduleConflict !== true);
  }
  if (Number(filterAlertPrograms) === 1) {
    display = display.filter((r) => Boolean(r?.alertNote));
  } else if (Number(filterAlertPrograms) === 2) {
    display = display.filter((r) => !r?.alertNote);
  }
  if (Number(filterCertificateSent) === 1) {
    display = display.filter((r) => r?.certificateSent === true);
  } else if (Number(filterCertificateSent) === 2) {
    display = display.filter((r) => r?.certificateSent !== true);
  }

  return { display: [...display], filtered0, filtered2 };
}

module.exports = {
  isOngoingCohort,
  isActiveEnrollmentProgram,
  parentHasActiveChildEnrollment,
  parentHasCompletedPastCohort,
  parentHasAnyCompletedProgram,
  filterParentsByCohortMode,
  filterParentsByCompletion,
  filterParentsByReferral,
  filterParentsByExternal,
  filterParentsByPrivacy,
  filterParentsByAssignedAdmin,
  applyManageParentFilters,
  filterProgramsByStatus,
  applyManageProgramFilters,
  getParentPrograms,
};
